/**
 * Big Toe Energy — product page add-ons for Horizon.
 *
 * <bte-bundle>  Quantity bundle (Buy 1 / 2 / 3). Keeps Horizon's own quantity
 *               input in sync with the selected option, so Horizon's normal
 *               add-to-cart sends the right quantity.
 * <bte-upsell>  Optional extra product. When checked, it is added to the cart
 *               right before Horizon adds the main product, so the cart drawer
 *               opens once with both lines.
 *
 * No dependencies. Horizon modules are only imported lazily, for price updates
 * after a variant change.
 */

const BYPASS_FLAG = 'bteBypass';
const BUSY_FLAG = 'bteBusy';
const UPSELL_PROPERTY = '_bte_upsell';

/** @param {string} formId */
function getForm(formId) {
  const form = formId ? document.getElementById(formId) : null;
  return form instanceof HTMLFormElement ? form : null;
}

/**
 * Writes the quantity into the product form. Uses Horizon's quantity input when
 * it exists, otherwise adds a hidden one.
 * @param {string} formId
 * @param {number} quantity
 */
function setFormQuantity(formId, quantity) {
  const form = getForm(formId);
  if (!form) return;

  let input = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'));
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'quantity';
    form.appendChild(input);
  }
  input.value = String(quantity);
}

/** @param {string} template @param {Record<string, string>} values */
function fillTemplate(template, values) {
  return template.replace(/\[(quantity|price|compare_price|amount_saved|percent_saved)\]/g, (_, key) => values[key] ?? '');
}

class BteBundle extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.sync();
    this.#listenForVariantChanges();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  get selectedInput() {
    return /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked') || this.querySelector('input[type="radio"]')
    );
  }

  get quantity() {
    return parseInt(this.selectedInput?.value || '1', 10) || 1;
  }

  sync() {
    setFormQuantity(this.dataset.formId || '', this.quantity);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') this.sync();
  };

  #listenForVariantChanges() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const { signal } = this.#abort;
    import('@shopify/events')
      .then(({ StandardEvents }) => {
        if (!StandardEvents?.productSelect || signal.aborted) return;
        section.addEventListener(
          StandardEvents.productSelect,
          (/** @type {any} */ event) => {
            Promise.resolve(event.promise)
              .then((result) => {
                const variant = result?.detail?.resource;
                if (variant) this.#updatePrices(variant);
                this.sync();
              })
              .catch(() => {});
          },
          { signal }
        );
      })
      .catch(() => {});
  }

  /** @param {{price: number, compare_at_price: number | null}} variant */
  async #updatePrices(variant) {
    let formatMoney;
    try {
      ({ formatMoney } = await import('@theme/money-formatting'));
    } catch {
      return;
    }

    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    const money = (/** @type {number} */ cents) => formatMoney(cents, format, currency);

    for (const option of this.querySelectorAll('[data-bte-option]')) {
      const el = /** @type {HTMLElement} */ (option);
      const quantity = parseInt(el.dataset.quantity || '1', 10);
      const percentOff = parseInt(el.dataset.percentOff || '0', 10);

      const unit = Math.round((variant.price * (100 - percentOff)) / 100);
      const total = unit * quantity;
      const compareUnit =
        variant.compare_at_price && variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price;
      const compareTotal = compareUnit * quantity;
      const saved = Math.max(compareTotal - total, 0);
      const percentSaved = compareTotal > 0 ? Math.round((saved * 100) / compareTotal) : 0;

      const values = {
        quantity: String(quantity),
        price: money(total),
        compare_price: money(compareTotal),
        amount_saved: money(saved),
        percent_saved: `${percentSaved}%`,
      };

      const price = el.querySelector('[data-bte-price]');
      if (price) price.textContent = values.price;

      const compare = /** @type {HTMLElement | null} */ (el.querySelector('[data-bte-compare]'));
      if (compare) {
        compare.textContent = values.compare_price;
        compare.hidden = saved <= 0;
      }

      const label = /** @type {HTMLElement | null} */ (el.querySelector('[data-bte-label]'));
      if (label?.dataset.template) label.textContent = fillTemplate(label.dataset.template, values);

      const caption = /** @type {HTMLElement | null} */ (el.querySelector('[data-bte-caption]'));
      if (caption?.dataset.template) {
        const needsSaving = /\[(amount_saved|percent_saved)\]/.test(caption.dataset.template);
        caption.textContent = fillTemplate(caption.dataset.template, values);
        caption.hidden = needsSaving && saved <= 0;
      }
    }
  }
}

class BteUpsell extends HTMLElement {
  get checked() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="checkbox"]'));
    return Boolean(input?.checked);
  }

  get variantId() {
    return this.dataset.variantId || '';
  }
}

if (!customElements.get('bte-bundle')) customElements.define('bte-bundle', BteBundle);
if (!customElements.get('bte-upsell')) customElements.define('bte-upsell', BteUpsell);

/**
 * Adds the checked upsells to the cart. Upsells sold "once per cart" are skipped
 * when they are already in the cart, so clicking Add twice doesn't add them twice.
 * @param {BteUpsell[]} upsells
 * @param {number} mainQuantity
 */
async function addUpsellsToCart(upsells, mainQuantity) {
  const routes = /** @type {any} */ (window).Theme?.routes || {};
  const cartAddUrl = routes.cart_add_url || '/cart/add.js';
  const cartUrl = `${routes.cart_url || '/cart'}.js`;

  let inCart = new Set();
  if (upsells.some((upsell) => upsell.dataset.quantityMode !== 'match')) {
    try {
      const cart = await fetch(cartUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(
        (response) => response.json()
      );
      inCart = new Set(
        (cart.items || [])
          .filter((/** @type {any} */ item) => item.properties && item.properties[UPSELL_PROPERTY])
          .map((/** @type {any} */ item) => String(item.variant_id))
      );
    } catch {
      // If the cart can't be read, add anyway.
    }
  }

  const items = upsells
    .filter((upsell) => upsell.dataset.quantityMode === 'match' || !inCart.has(upsell.variantId))
    .map((upsell) => ({
      id: Number(upsell.variantId),
      quantity: upsell.dataset.quantityMode === 'match' ? mainQuantity : 1,
      properties: { [UPSELL_PROPERTY]: '1' },
    }));

  if (items.length === 0) return;

  await fetch(cartAddUrl, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items }),
  });
}

/**
 * Runs before Horizon's own submit handler (window capture fires before
 * Horizon's document-level capture listener).
 */
window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    // Read the attribute: Horizon's form has an <input name="id">, which shadows `form.id`.
    const formId = form.getAttribute('id');
    if (!formId) return;

    const formSelector = `[data-form-id="${CSS.escape(formId)}"]`;
    const bundle = /** @type {BteBundle | null} */ (document.querySelector(`bte-bundle${formSelector}`));
    bundle?.sync();

    if (form.dataset[BYPASS_FLAG]) {
      delete form.dataset[BYPASS_FLAG];
      return;
    }

    const upsells = /** @type {BteUpsell[]} */ (
      Array.from(document.querySelectorAll(`bte-upsell${formSelector}`))
    ).filter((upsell) => upsell.checked && upsell.variantId);

    if (upsells.length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (form.dataset[BUSY_FLAG]) return;
    form.dataset[BUSY_FLAG] = 'true';

    const quantityInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'));
    const mainQuantity = bundle ? bundle.quantity : parseInt(quantityInput?.value || '1', 10) || 1;

    addUpsellsToCart(upsells, mainQuantity)
      .catch(() => {
        // The main product is still added below.
      })
      .finally(() => {
        delete form.dataset[BUSY_FLAG];
        form.dataset[BYPASS_FLAG] = 'true';
        form.requestSubmit();
      });
  },
  { capture: true }
);
